/**
 * useWorkspaceChat - Chat and Streaming Logic Hook
 * 
 * Extracts all chat/streaming logic from WorkspacePage.tsx:
 * - Message sending and streaming
 * - Stream event handling (tools, content, errors)
 * - Generation stopping
 * - Thinking steps display
 * - Plan document updates
 */

import { useRef, useState, useCallback } from 'react';
import { authService } from '../services/auth-service';
import { startAIStream, AIStreamController, cancelConversation } from '../services/streaming';
import { websocketService } from '../services/websocket-service';
import { conversationsApi } from '../services/api';
import type { AeroDocument, Section } from '../types/document-schema';
import type { DiagramState } from '../types/diagram-types';
import type { DocumentOperation } from '../types/document-operations';
import type { ConversationLink } from '@aerotage/shared';
import { logger } from '../utils/logger';

export interface ThinkingStep {
  thought: string;
  icon?: string;
}

export type ThinkingPhase = 'idle' | 'thinking' | 'executing' | 'solidifying' | 'complete';

export interface ThinkingActivity {
  id: string;
  type: 'planning' | 'executing' | 'complete' | 'tool_start' | 'tool_complete';
  icon: string;
  message: string;
  toolName?: string;
  timestamp: Date;
}

export interface StreamingProgress {
  turn: number;
  maxTurns: number;
  activeTools: Array<{ tool: string; icon: string; status: string }>;
  currentAction?: {
    icon: string;
    text: string;
    type: 'search' | 'edit' | 'validate' | 'thinking' | 'develop';
  };
  // Worker delegation
  delegated?: boolean;
  jobId?: string;
  // Development progress (for 'develop' mode)
  developProgress?: DevelopProgress;
}

export interface DevelopProgress {
  phase: 'analyzing' | 'generating' | 'compiling' | 'testing' | 'fixing' | 'building' | 'deploying' | 'analyze';
  iteration: number;
  maxIterations: number;
  detail: string;
  filesModified?: string[];
  compileErrors?: number;
  testsStatus?: {
    passed: number;
    failed: number;
    total: number;
  };
  previewUrl?: string;
}

export interface ActiveEditDocument {
  documentNumber: string;
  projectId: string;
  jsonDocument?: AeroDocument;
  version?: string;
  s3VersionId?: string; // S3 version ID for AI undo functionality
  s3JsonKey?: string;   // S3 path for document save
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  contextWindow: number;
}

export interface OpenedDocument {
  tabId: number;
  documentNumber?: string;
  projectId?: string;
  jsonDocument?: AeroDocument;
  s3JsonKey?: string;
  title: string;
  tabType?: string;
  diagramId?: string;
}

export interface PageContext {
  documentContext?: {
    documentNumber?: string;
    projectId?: string;
    revision?: string;
  };
  personaId?: string;  // Active persona for this session (from project or user default)
}

interface ChatMessage {
  role: string;
  content: string;
  id?: string;
  timestamp?: string;
}

interface ConversationContext {
  projectId?: string;
  documentId?: string;
  mode?: string;
}

interface UseWorkspaceChatDeps {
  // From AIContext -- conversation-scoped functions
  messages: ChatMessage[];
  addMessage: (conversationId: string, msg: ChatMessage) => void;
  updateLastMessage: (conversationId: string, content: string) => void;
  setLastMessageResolvedModel: (conversationId: string, resolvedModel: { modelId: string; reason: string }) => void;
  markLastMessageStopped: (conversationId: string) => void;
  isProcessing: boolean;
  setIsProcessing: (conversationId: string, val: boolean) => void;
  aiMode: 'ask' | 'agent' | 'plan' | 'diagram';
  selectedModel: string;
  pageContext: PageContext;
  streamingProgress: StreamingProgress | null;
  setStreamingProgress: (conversationId: string, val: StreamingProgress | null | ((prev: StreamingProgress | null) => StreamingProgress | null)) => void;
  activeConversationId: string | null;
  ensureBuffer: (conversationId: string) => void;
  // Thinking overlay -- conversation-scoped setters from AIContext
  setThinkingPhase: (conversationId: string, phase: ThinkingPhase) => void;
  addThinkingActivity: (conversationId: string, activity: ThinkingActivity) => void;
  updateThinkingActivities: (conversationId: string, updater: (prev: ThinkingActivity[]) => ThinkingActivity[]) => void;
  clearThinkingActivities: (conversationId: string) => void;
  setPreviewText: (conversationId: string, text: string) => void;
  setDebugState: (conversationId: string, state: { chainCount: number; turnCount: number; elapsedMs: number }) => void;
  // Derived from active buffer (read-only)
  thinkingPhase: ThinkingPhase;
  thinkingActivities: ThinkingActivity[];
  previewText: string;
  debugState: { chainCount: number; turnCount: number; elapsedMs: number };
  // (registerStopGeneration not needed in hook -- WorkspacePage handles registration directly)
  createConversation: (title?: string, context?: ConversationContext) => Promise<string>;
  loadConversations: () => void;
  updateConversationTitle: (id: string, title: string) => void;
  allowedScopes: string[];
  allowedProjectIds: string[];
  openedDocuments: OpenedDocument[];
  activeEditDocument: ActiveEditDocument | null;
  activeTabId: number | null;
  // Link-based architecture
  conversationLinks: ConversationLink[];
  // Diagram state
  setDiagramState?: (state: DiagramState | null) => void;
  activeDiagramId?: string | null;
  
  // Mermaid diagram update (for flowcharts)
  setMermaidVersion?: (mermaidDiagramId: string, version: number) => void;
  
  // Coding project update (for AI development)
  setCodingProjectVersion?: (codingProjectId: string, version: number) => void;
  
  // Document state management - for direct document_sync updates
  updateOpenedDocument: (tabId: number, updates: Partial<{ jsonDocument: AeroDocument; version: string; s3VersionId: string; s3JsonKey: string }>) => void;
  
  // Other deps
  navigate: (path: string) => void;
  scrollToBottom: () => void;
}

/**
 * Tool action display mapping
 */
const getToolAction = (tool: string, query?: string) => {
  const actionMap: Record<string, { icon: string; text: string; type: 'search' | 'edit' | 'validate' | 'thinking' }> = {
    'semantic_search_document': { 
      icon: '🔍', 
      text: query ? `Searching: "${query.substring(0, 50)}${query.length > 50 ? '...' : ''}"` : 'Searching document...', 
      type: 'search' 
    },
    'exact_search_document': {
      icon: '🎯',
      text: query ? `Finding: "${query.substring(0, 50)}${query.length > 50 ? '...' : ''}"` : 'Exact search...',
      type: 'search'
    },
    'search_reference_documents': { 
      icon: '📚', 
      text: 'Checking industry standards...', 
      type: 'search' 
    },
    'search_company_documents': { 
      icon: '📋', 
      text: 'Checking company documents...', 
      type: 'search' 
    },
    'traverse_document_graph': {
      icon: '🗂️',
      text: 'Analyzing document structure...',
      type: 'search'
    },
    'list_reference_documents': {
      icon: '📖',
      text: 'Listing available standards...',
      type: 'search'
    },
    'edit_document': {
      icon: '✏️',
      text: 'Editing document...',
      type: 'edit'
    },
    'edit_plan_document': {
      icon: '📝',
      text: 'Creating execution plan...',
      type: 'edit'
    },
    // Diagram tools
    'route_wire': {
      icon: '🔌',
      text: 'Routing wire path...',
      type: 'edit'
    },
    'place_connector': {
      icon: '📍',
      text: 'Placing connector...',
      type: 'edit'
    },
    'query_region': {
      icon: '🔎',
      text: 'Checking region congestion...',
      type: 'search'
    },
    'get_diagram_state': {
      icon: '📊',
      text: 'Getting diagram state...',
      type: 'search'
    },
    'render_diagram': {
      icon: '🖼️',
      text: 'Rendering diagram...',
      type: 'edit'
    },
    'analyze_region': {
      icon: '🔬',
      text: 'Analyzing region...',
      type: 'validate'
    },
  };
  
  return actionMap[tool] || { icon: '⚙️', text: 'Processing...', type: 'thinking' as const };
};

export function useWorkspaceChat(deps: UseWorkspaceChatDeps) {
  const {
    messages,
    addMessage,
    updateLastMessage,
    setLastMessageResolvedModel,
    markLastMessageStopped,
    isProcessing: _isProcessing, // Not used in hook - caller handles processing checks
    setIsProcessing,
    aiMode,
    selectedModel,
    pageContext,
    setStreamingProgress,
    activeConversationId,
    ensureBuffer,
    // Thinking overlay -- conversation-scoped setters
    setThinkingPhase,
    addThinkingActivity,
    updateThinkingActivities,
    clearThinkingActivities,
    setPreviewText,
    setDebugState,
    // Derived (read-only, from active buffer)
    thinkingPhase,
    thinkingActivities,
    previewText,
    debugState,
    createConversation,
    loadConversations,
    updateConversationTitle,
    allowedScopes,
    allowedProjectIds,
    openedDocuments,
    activeEditDocument,
    activeTabId,
    conversationLinks,
    setDiagramState,
    activeDiagramId,
    setMermaidVersion,
    setCodingProjectVersion,
    updateOpenedDocument,
    navigate,
    scrollToBottom,
  } = deps;

  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  
  // NOTE: previewText, thinkingPhase, thinkingActivities, debugState are now
  // in the per-conversation buffer (AIContext), not local state.
  // The hook receives setters that target the specific conversation's buffer.
  
  // Refs for streaming state -- keyed by conversationId for concurrent streams
  const streamControllersRef = useRef<Map<string, AIStreamController>>(new Map());
  const streamingTextRef = useRef<string>('');
  
  // Refs for batched preview text updates (reduces jerkiness)
  const previewTextBufferRef = useRef<string>('');
  const previewUpdateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const PREVIEW_UPDATE_INTERVAL = 100; // Update UI every 100ms for smooth rendering
  
  // Track section updates for sync verification with document_sync_meta
  const sectionUpdateCounterRef = useRef<number>(0);
  
  // Refs for values that need to stay current in long-running stream handlers
  // This fixes stale closure issues during Plan mode execution
  const openedDocumentsRef = useRef(openedDocuments);
  const updateOpenedDocumentRef = useRef(updateOpenedDocument);
  const activeEditDocumentRef = useRef(activeEditDocument);
  
  // Keep refs synchronized with current values
  openedDocumentsRef.current = openedDocuments;
  updateOpenedDocumentRef.current = updateOpenedDocument;
  activeEditDocumentRef.current = activeEditDocument;

  interface DocumentContext {
    documentNumber?: string;
    projectId?: string;
    jsonDocument?: AeroDocument;
    revision?: string;
    currentRevision?: string;
    s3VersionId?: string;
    s3JsonKey?: string;
    title?: string;
    // Document context isolation fields
    source?: 'activeEdit' | 'openedTabs' | 'linkOnly';
    canEdit?: boolean;
    mismatchWarning?: string;
  }

  /**
   * Build document context for AI request
   * Link-based architecture: Document context is included when a document is linked
   */
  const buildDocumentContext = useCallback(() => {
    let documentContext: DocumentContext | null = null;
    
    // Link-based architecture: Check if there's a document or plan link in the conversation
    // Plans use the same document infrastructure, so they're treated similarly here
    const documentLink = conversationLinks.find(l => l.artifactType === 'document' || l.artifactType === 'plan');
    
    // CRITICAL: Only provide edit context if activeEditDocument MATCHES the linked document
    // This prevents cross-document editing when user switches conversations
    const isLinkedDocumentActive = documentLink && 
      activeEditDocument && 
      activeEditDocument.documentNumber === documentLink.artifactId;
    
    if (isLinkedDocumentActive) {
      // Document is linked AND the CORRECT document is active for editing
      const docRevision = activeEditDocument.jsonDocument?.metadata?.currentRevision || 'Draft';
      documentContext = {
        documentNumber: activeEditDocument.documentNumber,
        projectId: activeEditDocument.projectId,
        jsonDocument: activeEditDocument.jsonDocument,
        revision: docRevision,
        currentRevision: docRevision,
        s3VersionId: activeEditDocument.s3VersionId, // For AI undo functionality
        s3JsonKey: activeEditDocument.s3JsonKey,     // For AI document save
        // Context isolation: linked doc matches active edit doc
        source: 'activeEdit',
        canEdit: true,
      };
      
      // Get CURRENT state from editor for server-side document operations
      const hasGetterFn = typeof window.__getCurrentEditorDocument === 'function';
      if (hasGetterFn) {
        const currentDoc = window.__getCurrentEditorDocument?.();
        if (currentDoc) {
          documentContext = { ...documentContext, jsonDocument: currentDoc };
        }
      }
    } else if (documentLink) {
      // Conversation is linked to a document, but it's not the active edit document
      // Try to find the linked document in opened documents (read-only context)
      const linkedDoc = openedDocuments.find(d => d.documentNumber === documentLink.artifactId);
      if (linkedDoc) {
        logger.debug('[useWorkspaceChat] Using linked document from opened tabs (read-only):', documentLink.artifactId);
        documentContext = {
          documentNumber: linkedDoc.documentNumber,
          projectId: linkedDoc.projectId,
          jsonDocument: linkedDoc.jsonDocument,
          s3VersionId: (linkedDoc as unknown as { s3VersionId?: string }).s3VersionId,
          // No s3JsonKey = read-only context, AI cannot save edits
          // Context isolation: linked doc in tabs but not active edit
          source: 'openedTabs',
          canEdit: false,
          mismatchWarning: `Document ${documentLink.artifactId} is open but not in edit mode. Click the edit button on the document tab to enable editing.`,
        };
      } else {
        // Linked document not found in any open tabs
        logger.warn('[useWorkspaceChat] Linked document not open:', {
          linkedDocument: documentLink.artifactId,
          activeDocument: activeEditDocument?.documentNumber,
          conversationId: activeConversationId,
        });
        // Return context with link info but no content
        return { 
          documentNumber: documentLink.artifactId,
          projectId: documentLink.projectId,
          // Context isolation: linked doc not loaded at all
          source: 'linkOnly',
          canEdit: false,
          mismatchWarning: `Linked document ${documentLink.artifactId} is not open. Please open the document to enable editing.`,
        } as DocumentContext;
      }
    } else if (activeTabId && openedDocuments.length > 0) {
      // No document link - provide read-only context from active tab
      const activeDoc = openedDocuments.find(d => d.tabId === activeTabId);
      if (activeDoc) {
        documentContext = {
          documentNumber: activeDoc.documentNumber,
          projectId: activeDoc.projectId,
          jsonDocument: activeDoc.jsonDocument,
          // Include diagramId for diagram tabs so Mermaid tools have project context
          diagramId: activeDoc.diagramId,
          s3VersionId: (activeDoc as unknown as { s3VersionId?: string }).s3VersionId,
          // Context isolation: no link, using active tab
          source: 'activeEdit',
          canEdit: true, // No link = explicit edit mode allowed
        };
      }
    }
    
    // Only fall back to pageContext if we have NO document link
    // (prevents wrong document being used when conversation is linked)
    if (!documentContext && !documentLink) {
      documentContext = pageContext.documentContext 
        ? { 
            ...pageContext.documentContext,
            // Context isolation: pageContext fallback with no link
            source: 'activeEdit' as const,
            canEdit: true, // No link = explicit edit mode allowed
          }
        : null;
    }
    
    return documentContext;
  }, [conversationLinks, activeTabId, openedDocuments, pageContext.documentContext, activeEditDocument]);

  /**
   * Send a message to the AI
   * Note: The isProcessing guard has been removed because the caller (handleSendMessage)
   * is now responsible for stopping any in-flight processing before calling sendMessage.
   * This enables the Claude-like "interrupt and send" behavior.
   * 
   * @param input - The message content to send
   * @param options - Optional parameters
   * @param options.modeOverride - Override the current aiMode for this request
   *   Useful when mode is changed immediately before sending (React state is async)
   */
  const sendMessage = useCallback(async (input: string, options?: { modeOverride?: 'ask' | 'agent' | 'plan' | 'diagram' | 'develop' }) => {
    if (!input.trim()) return;
    
    // Use override mode if provided (handles async React state updates)
    const effectiveMode = options?.modeOverride || aiMode;

    const userMessage = {
      role: 'user' as const,
      content: input.trim(),
      timestamp: new Date(),
    };

    let convId = activeConversationId;
    if (!convId) {
      convId = await createConversation(undefined, pageContext);
      navigate(`/workspace/${convId}`);
    }

    // Ensure buffer exists for this conversation before writing
    ensureBuffer(convId);

    // All mutation calls use the captured convId -- this is the key architectural change.
    // Even if the user switches tabs, these writes go to the correct conversation buffer.
    addMessage(convId, userMessage);

    const assistantPlaceholder = {
      role: 'assistant' as const,
      content: '',
      timestamp: new Date(),
    };
    addMessage(convId, assistantPlaceholder);
    setIsProcessing(convId, true);
    setThinkingSteps([]);
    setPreviewText(convId, '');
    setThinkingPhase(convId, 'thinking');
    clearThinkingActivities(convId);
    setDebugState(convId, { chainCount: 0, turnCount: 0, elapsedMs: 0 });
    sectionUpdateCounterRef.current = 0;  // Reset section update counter for sync verification
    streamingTextRef.current = '';

    try {
      const tokens = await authService.getTokens();
      const documentContext = buildDocumentContext();
      
      logger.debug('[useWorkspaceChat] Document context for AI:', {
        hasDocumentContext: !!documentContext,
        documentNumber: documentContext?.documentNumber,
        projectId: documentContext?.projectId,
        hasJsonDocument: !!documentContext?.jsonDocument,
        conversationLinksCount: conversationLinks.length,
        hasDocumentLink: conversationLinks.some(l => l.artifactType === 'document' || l.artifactType === 'plan'),
        activeEditDocument: !!activeEditDocument,
      });
      
      let fullContent = '';
      let needsSeparator = false; // Add separator after tool completes before new text
      let inFinalResponse = false; // Once true, update message directly, hide ghost text
      
      // NOTE: isStreamStillActive() guard REMOVED.
      // With per-conversation buffers, all stream handlers write to the buffer
      // identified by the captured `convId`. Tab switching doesn't affect correctness.
      
      const streamHandlers = {
        onInit: (event: { maxTurns?: number }) => {
          setStreamingProgress(convId, {
            turn: 0,
            maxTurns: event.maxTurns || 8,
            activeTools: []
          });
          scrollToBottom();
        },
        
        onProgress: (message: string) => {
          const turnMatch = message.match(/Turn (\d+)\/(\d+)/);
          if (turnMatch) {
            setStreamingProgress(convId, prev => prev ? {
              ...prev,
              turn: parseInt(turnMatch[1]),
              activeTools: []
            } : null);
          }
        },
        
        onToolStart: (tool: string, query?: string) => {
          const action = getToolAction(tool, query);
          setStreamingProgress(convId, prev => prev ? {
            ...prev,
            currentAction: action,
            activeTools: [...prev.activeTools, { tool, icon: action.icon, status: 'active' }]
          } : null);
          
          // Add to thinking activities
          setThinkingPhase(convId, 'executing');
          addThinkingActivity(convId, {
            id: `tool-${Date.now()}-${tool}`,
            type: 'tool_start',
            icon: action.icon,
            message: action.text,
            toolName: tool,
            timestamp: new Date(),
          });
          
          scrollToBottom();
        },
        
        onToolComplete: (tool: string, result?: string) => {
          setStreamingProgress(convId, prev => {
            if (!prev) return null;
            return {
              ...prev,
              activeTools: prev.activeTools.map(t => 
                t.tool === tool ? { ...t, status: 'complete' } : t
              )
            };
          });
          
          // Update the activity to show completion
          updateThinkingActivities(convId, prev => {
            const lastToolActivity = [...prev].reverse().find(a => a.toolName === tool && a.type === 'tool_start');
            if (lastToolActivity) {
              return prev.map(a => 
                a.id === lastToolActivity.id 
                  ? { ...a, type: 'tool_complete' as const, message: result || `${a.message} ✓` }
                  : a
              );
            }
            return prev;
          });
          
          // Set flag to add separator before next text (prevents "you.Good!" concatenation)
          needsSeparator = true;
          
          // Update coding project version when relevant tools complete
          // This triggers a refetch in CodingProjectViewer
          const codingTools = [
            'compile_code', 'run_tests', 'build_project', 'lint_code',
            'working_write_file', 'working_delete_file', 'commit_files',
          ];
          if (codingTools.includes(tool) && setCodingProjectVersion) {
            const codingLink = conversationLinks.find(l => l.artifactType === 'coding');
            if (codingLink?.artifactId) {
              logger.debug('[useWorkspaceChat] Updating coding project version:', codingLink.artifactId);
              setCodingProjectVersion(codingLink.artifactId, Date.now());
            }
          }
        },
        
        onContent: (_delta: string) => {
          // Content handled by onTextDelta with batched updates
        },
        
        onTextDelta: (delta: string) => {
          // Add line break separator after tool completion to prevent concatenation
          if (needsSeparator && fullContent.length > 0) {
            fullContent += '\n\n';
            needsSeparator = false;
          }
          fullContent += delta;
          
          // Check if we've entered final response mode
          if (!inFinalResponse) {
            const finalResponseMatch = fullContent.match(/(?:^|\n)(#{1,3}\s.+|Based on my review|Based on my analysis|Based on the |I've identified|I've found|Here are|Here is the|The following|After reviewing)/);
            
            if (finalResponseMatch) {
              // Transition from ghost text to real message
              inFinalResponse = true;
              
              // Clear any pending preview timer
              if (previewUpdateTimerRef.current) {
                clearTimeout(previewUpdateTimerRef.current);
                previewUpdateTimerRef.current = null;
              }
              
              // Trigger fade-out animation for ghost text
              setThinkingPhase(convId, 'solidifying');
              
              // Extract final response
              const startIndex = fullContent.indexOf(finalResponseMatch[1]);
              const finalResponse = fullContent.substring(startIndex);
              
              // Delay showing response to let ghost text fade out gracefully
              setTimeout(() => {
                setPreviewText(convId, '');
                previewTextBufferRef.current = '';
                updateLastMessage(convId, finalResponse);
                setStreamingProgress(convId, prev => prev ? { ...prev, currentAction: undefined } : null);
              }, 250); // 250ms fade-out animation
            } else {
              // Still in tool/thinking phase - update ghost text with batching
              previewTextBufferRef.current = fullContent;
              
              if (!previewUpdateTimerRef.current) {
                previewUpdateTimerRef.current = setTimeout(() => {
                  setPreviewText(convId, previewTextBufferRef.current);
                  previewUpdateTimerRef.current = null;
                }, PREVIEW_UPDATE_INTERVAL);
              }
            }
          } else {
            // In final response mode - update message directly
            const finalResponseMatch = fullContent.match(/(?:^|\n)(#{1,3}\s.+|Based on my review|Based on my analysis|Based on the |I've identified|I've found|Here are|Here is the|The following|After reviewing)/);
            if (finalResponseMatch) {
              const startIndex = fullContent.indexOf(finalResponseMatch[1]);
              const finalResponse = fullContent.substring(startIndex);
              updateLastMessage(convId, finalResponse);
            }
          }
        },
        
        onEditPreview: (explanation: string, operationCount?: number) => {
          logger.debug('[useWorkspaceChat] Edit preview:', explanation, operationCount);
        },
        
        onDocumentOperation: (operation: DocumentOperation) => {
          if (typeof window.__queueDocumentOperation === 'function') {
            window.__queueDocumentOperation(operation);
          }
        },
        
        onSectionUpdate: (sectionId: string, section: Section, operationType: string, changedBlockIds?: string[], documentNumber?: string) => {
          sectionUpdateCounterRef.current++;
          // Route update to the correct document
          // If documentNumber matches active document or is undefined (legacy), update directly
          const isActiveDoc = !documentNumber || (activeEditDocument?.documentNumber === documentNumber);
          
          if (isActiveDoc) {
            if (typeof window.__handleSectionUpdate === 'function') {
              window.__handleSectionUpdate(sectionId, section, operationType, changedBlockIds);
            }
          } else {
            // Update for a different document (e.g., target doc in Plan mode)
            // Queue for refresh when that tab becomes active
            logger.debug('[useWorkspaceChat] Section update for external document:', documentNumber, sectionId);
            // TODO: Implement external document update queue if needed
          }
        },
        
        onSectionDelete: (sectionId: string, documentNumber?: string) => {
          const isActiveDoc = !documentNumber || (activeEditDocument?.documentNumber === documentNumber);
          
          if (isActiveDoc) {
            if (typeof window.__handleSectionDelete === 'function') {
              window.__handleSectionDelete(sectionId);
            }
          } else {
            logger.debug('[useWorkspaceChat] Section delete for external document:', documentNumber, sectionId);
          }
        },
        
        // Note: plan_document_update handler removed - plans now use standard document SSE streaming
        
        onDocumentSync: (document: AeroDocument, version: string, s3VersionId?: string, preAIVersionId?: string, documentNumber?: string) => {
          // documentNumber from SSE event, or fall back to document.docId
          const targetDocNumber = documentNumber || document?.docId;
          
          logger.debug('[useWorkspaceChat] document_sync received:', {
            version,
            s3VersionId,
            preAIVersionId,
            docId: document?.docId,
            documentNumber: targetDocNumber,
            documentVersion: document?.version
          });
          
          // Route sync to the correct document
          const isActiveDoc = activeEditDocument?.documentNumber === targetDocNumber;
          
          // CRITICAL: Update document state at the context level FIRST
          // This ensures lastSavedJson is updated regardless of whether StructuredEditor is mounted
          if (isActiveDoc && activeEditDocument && activeTabId !== null) {
            logger.debug('[useWorkspaceChat] Updating openedDocument for tab:', activeTabId, 'version:', document.version);
            updateOpenedDocument(activeTabId, {
              jsonDocument: document,
              version: document.version,
              s3VersionId: s3VersionId
            });
            
            // Also propagate to TipTap editor if handler is mounted (for live content refresh)
            if (typeof window.__handleDocumentSync === 'function') {
              logger.debug('[useWorkspaceChat] Calling window.__handleDocumentSync');
              window.__handleDocumentSync(document, version, s3VersionId, preAIVersionId);
            } else {
              logger.warn('[useWorkspaceChat] window.__handleDocumentSync NOT defined - StructuredEditor may not be mounted');
            }
          } else if (!isActiveDoc && targetDocNumber) {
            // Update for a different document (e.g., target doc in Plan mode, or plan doc when viewing target)
            // Find the tab for this document and update it
            // CRITICAL: Use ref to get current openedDocuments - avoids stale closure during long Plan execution
            const currentOpenedDocs = openedDocumentsRef.current;
            const externalDoc = currentOpenedDocs.find(d => d.documentNumber === targetDocNumber);
            if (externalDoc && externalDoc.tabId !== undefined) {
              logger.debug('[useWorkspaceChat] Updating external document:', targetDocNumber, 'tab:', externalDoc.tabId, 'version:', document.version);
              // Use ref for updateOpenedDocument as well
              updateOpenedDocumentRef.current(externalDoc.tabId, {
                jsonDocument: document,
                version: document.version,
                s3VersionId: s3VersionId
              });
            } else {
              logger.debug('[useWorkspaceChat] Document sync for non-open document:', targetDocNumber, '- will refresh on tab switch');
            }
          }
        },
        
        onDocumentSyncMeta: (version: string, s3VersionId?: string, operationCount?: number, sectionCount?: number, documentNumber?: string) => {
          logger.debug('[useWorkspaceChat] document_sync_meta received:', {
            version,
            s3VersionId,
            operationCount,
            sectionCount,
            documentNumber,
            localSectionUpdates: sectionUpdateCounterRef.current
          });
          
          // Route sync_meta to the correct document
          const isActiveDoc = !documentNumber || (activeEditDocument?.documentNumber === documentNumber);
          
          if (!isActiveDoc) {
            // Metadata update for a different document - log and skip active doc updates
            logger.debug('[useWorkspaceChat] Sync meta for external document:', documentNumber);
            return;
          }
          
          // Check if we received all section_update events
          if (operationCount !== undefined && sectionUpdateCounterRef.current !== operationCount) {
            logger.warn(`[useWorkspaceChat] Sync drift detected: received ${sectionUpdateCounterRef.current} section_updates, server sent ${operationCount}`);
            // Document may be out of sync - user should refresh to get full document
            // For now just log, future: could trigger automatic refresh
          }
          
          // Update version tracking via window handler if available
          if (typeof window.__handleDocumentSyncMeta === 'function') {
            window.__handleDocumentSyncMeta(version, s3VersionId, operationCount, sectionCount);
          }
          
          // Update context-level version tracking
          if (activeEditDocument && activeTabId !== null) {
            logger.debug('[useWorkspaceChat] Updating version metadata for tab:', activeTabId, 'version:', version);
            updateOpenedDocument(activeTabId, {
              version: version,
              s3VersionId: s3VersionId
            });
          }
        },
        
        onOperationError: (error: string, operation: DocumentOperation) => {
          logger.error('[useWorkspaceChat] Operation failed:', error, operation);
          updateLastMessage(convId, `⚠️ **Edit Failed:** ${error}\n\nThe AI attempted to edit the document but the operation could not be applied.`);
        },
        
        onWarning: (message: string) => {
          logger.warn('AI Warning:', message);
        },
        
        onHandoff: (message: string) => {
          logger.debug('[useWorkspaceChat] Handoff:', message);
          // Work is continuing in background worker - keep processing state
        },
        
        onError: (error: string) => {
          updateLastMessage(convId, `Error: ${error}`);
          setStreamingProgress(convId, null);
          setIsProcessing(convId, false);
        },
        
        onDone: (message: string, _edits?: DocumentOperation[], _tokenUsage?: { inputTokens: number; outputTokens: number; totalTokens: number }, _truncated?: boolean, _stopReason?: string, resolvedModel?: { modelId: string; reason: string }) => {
          let finalContent = message || '';
          if (!finalContent && fullContent) {
            const finalMatch = fullContent.match(/(?:^|\n)(#{1,3}\s.+|Based on my review|Based on my analysis|Based on the |I've identified|I've found|Here are|Here is the|The following|After reviewing)/);
            if (finalMatch) {
              const startIndex = fullContent.indexOf(finalMatch[1]);
              finalContent = fullContent.substring(startIndex);
            } else {
              finalContent = fullContent;
            }
          }
          if (finalContent) {
            updateLastMessage(convId, finalContent);
          }
          
          if (resolvedModel) {
            setLastMessageResolvedModel(convId, resolvedModel);
          }
          
          // Clear any pending preview update timer and flush final content
          if (previewUpdateTimerRef.current) {
            clearTimeout(previewUpdateTimerRef.current);
            previewUpdateTimerRef.current = null;
          }
          setPreviewText(convId, previewTextBufferRef.current);
          previewTextBufferRef.current = '';
          
          // Trigger solidifying transition
          setThinkingPhase(convId, 'solidifying');
          setTimeout(() => {
            setThinkingPhase(convId, 'complete');
            setPreviewText(convId, '');
            clearThinkingActivities(convId);
          }, 300); // Match CSS transition duration
          
          scrollToBottom();
          
          // Clean up processing state
          setStreamingProgress(convId, null);
          setIsProcessing(convId, false);
          
          // Clean up stream controller ref
          streamControllersRef.current.delete(convId);
          
          if (messages.length <= 2) {
            setTimeout(async () => {
              try {
                const conversation = await conversationsApi.get(convId!);
                if (conversation.title && conversation.title !== 'New Chat') {
                  loadConversations();
                }
              } catch (error) {
                logger.error('Failed to fetch updated title:', error);
              }
            }, 3000);
          }
        },
        
        onTitleUpdate: (cId: string, title: string) => {
          logger.debug('[useWorkspaceChat] Title update received:', { cId, title, convId });
          // Update title immediately in local state for instant UI feedback
          updateConversationTitle(cId, title);
          // Also reload conversations to sync with backend
          if (cId === convId) {
            loadConversations();
          }
        },
        
        onThinking: (thought: string, toolName?: string, icon?: string, phase?: string) => {
          setThinkingSteps(prev => [...prev.slice(-4), { thought, icon }]);
          
          // Add to thinking activities for ThinkingOverlay
          const activityType = phase === 'complete' ? 'complete' : phase === 'executing' ? 'executing' : 'planning';
          addThinkingActivity(convId, {
            id: `thinking-${Date.now()}`,
            type: activityType,
            icon: icon || '💭',
            message: thought,
            toolName,
            timestamp: new Date(),
          });
        },
        
        // Diagram state updates (for DIAGRAM mode)
        onDiagramState: (state: DiagramState) => {
          logger.debug('[DIAGRAM] onDiagramState called, setDiagramState exists:', !!setDiagramState);
          logger.debug('[DIAGRAM] Received diagram_state:', {
            deviceCount: state.devices?.length,
            wireCount: state.wires?.length,
            version: state.version,
            devices: state.devices?.map(d => ({ id: d.id, position: d.position }))
          });
          if (setDiagramState) {
            setDiagramState({
              devices: state.devices || [],
              wires: state.wires || [],
              crossings: state.crossings || [],
              version: state.version, // Pass version for Boeing WDM SVG refresh trigger
            });
          }
        },
        
        // Mermaid diagram updates (for flowcharts)
        onMermaidUpdate: (update: { mermaidDiagramId: string; svgUrl: string; title?: string; mermaidType?: string; version: number }) => {
          logger.debug('[MERMAID] onMermaidUpdate called, setMermaidVersion exists:', !!setMermaidVersion);
          logger.debug('[MERMAID] Received mermaid_update:', {
            mermaidDiagramId: update.mermaidDiagramId,
            svgUrl: update.svgUrl?.substring(0, 50) + '...',
            version: update.version,
          });
          if (setMermaidVersion) {
            setMermaidVersion(update.mermaidDiagramId, update.version);
          }
        },
        
        // Worker delegation handlers
        onDelegated: async (jobId: string, _message: string, _connectionId?: string) => {
          logger.debug(`[useWorkspaceChat] Job delegated: ${jobId}`);
          
          // Job is now being processed by worker - results will come via WebSocket
          // Register this job's handlers on WebSocket so we receive worker events
          websocketService.registerJobHandlers(jobId, {
            // Forward all events to the same handlers we defined above
            onInit: streamHandlers.onInit,
            onProgress: streamHandlers.onProgress,
            onToolStart: streamHandlers.onToolStart,
            onToolComplete: streamHandlers.onToolComplete,
            onContent: streamHandlers.onContent,
            onTextDelta: streamHandlers.onTextDelta,
            onWarning: streamHandlers.onWarning,
            onError: streamHandlers.onError,
            onDone: (msg, edits, tokenUsage) => {
              streamHandlers.onDone?.(msg, edits, tokenUsage);
              // Cleanup job handlers after done
              websocketService.unregisterJobHandlers(jobId);
            },
            onPlanDone: (msg, edits) => {
              streamHandlers.onDone?.(msg, edits);
              websocketService.unregisterJobHandlers(jobId);
            },
            onPlanError: (error) => {
              streamHandlers.onError?.(error);
              websocketService.unregisterJobHandlers(jobId);
            },
            onHandoff: streamHandlers.onHandoff,
            onThinking: streamHandlers.onThinking,
            onTitleUpdate: streamHandlers.onTitleUpdate,
            onEditPreview: streamHandlers.onEditPreview,
            onSectionUpdate: streamHandlers.onSectionUpdate,
            onSectionDelete: streamHandlers.onSectionDelete,
            onDocumentSync: streamHandlers.onDocumentSync,
            onDocumentSyncMeta: streamHandlers.onDocumentSyncMeta,
            onOperationError: streamHandlers.onOperationError,
            onDebugState: streamHandlers.onDebugState,
            onDebugChain: streamHandlers.onDebugChain,
            // Diagram state handlers - required for diagram mode auto-refresh after render_diagram
            onDiagramState: streamHandlers.onDiagramState,
            onMermaidUpdate: streamHandlers.onMermaidUpdate,
            // Develop feature handlers
            onDevelopProgress: streamHandlers.onDevelopProgress,
            onDevelopComplete: streamHandlers.onDevelopComplete,
            onDevelopPaused: streamHandlers.onDevelopPaused,
            // Micro-unit handlers (requirements-based micro-coding pipeline)
            // Map micro_unit_* events to develop handlers for UI state transitions
            onMicroUnitProgress: (data: Record<string, unknown>) => {
              streamHandlers.onDevelopProgress?.({
                jobId: (data.jobId as string) || '',
                phase: (data.phase as string) || 'unknown',
                iteration: (data.iteration as number) || 0,
                maxIterations: 100,
                message: (data.message as string) || '',
              });
            },
            onMicroUnitComplete: (data: Record<string, unknown>) => {
              // Per-requirement completes have reqId but no result -- skip those.
              // Only trigger the chat completion on the project-level complete (has result with filesCreated).
              if (data.reqId && !data.result) return;

              streamHandlers.onDevelopComplete?.({
                jobId: (data.jobId as string) || '',
                result: (data.result as Record<string, unknown>) || { success: true },
                message: (data.message as string) || 'Development complete',
              });
              websocketService.unregisterJobHandlers(jobId);
            },
            onMicroUnitPaused: (data: Record<string, unknown>) => {
              streamHandlers.onDevelopPaused?.({
                jobId: (data.jobId as string) || '',
                reason: (data.reason as string) || 'unknown',
                message: (data.message as string) || '',
                errors: (data.errors as string[]) || [],
              });
              websocketService.unregisterJobHandlers(jobId);
            },
          });
          
          setStreamingProgress(convId, prev => prev ? {
            ...prev,
            delegated: true,
            jobId,
          } : { turn: 0, maxTurns: 200, activeTools: [], delegated: true, jobId });
        },
        
        onDebugState: (state: { jobId: string; turnCount: number; chainCount: number; elapsedMs: number; timeoutMs: number }) => {
          logger.debug(`[useWorkspaceChat Debug] Job ${state.jobId}: turn=${state.turnCount}, chain=${state.chainCount}, elapsed=${state.elapsedMs}ms`);
          // Update debug state for ThinkingOverlay
          setDebugState(convId, {
            chainCount: state.chainCount,
            turnCount: state.turnCount,
            elapsedMs: state.elapsedMs,
          });
        },
        
        onDebugChain: (data: { jobId: string; chainCount: number; turnCount: number; reason: string }) => {
          logger.debug(`[useWorkspaceChat Debug] Chain triggered: ${data.reason}, chain #${data.chainCount}`);
        },
        
        // Develop feature autonomous loop handlers
        onDevelopProgress: (data: { jobId: string; phase: string; iteration: number; maxIterations: number; message: string; success?: boolean; errorCount?: number }) => {
          logger.debug(`[useWorkspaceChat] Develop progress: phase=${data.phase}, iteration=${data.iteration}/${data.maxIterations}`);
          
          // Update preview text with progress
          setPreviewText(convId, `🔄 ${data.phase}: ${data.message} (iteration ${data.iteration}/${data.maxIterations})`);
          
          // Update streaming progress for ThinkingOverlay
          setStreamingProgress(convId, prev => prev ? {
            ...prev,
            activeTools: [`develop_feature:${data.phase}`],
          } : { turn: data.iteration, maxTurns: data.maxIterations, activeTools: [`develop_feature:${data.phase}`] });
        },
        
        onDevelopComplete: (data: { jobId: string; result: { success?: boolean; filesCreated?: string[]; filesModified?: string[]; previewUrl?: string | null; pauseReason?: string }; message: string }) => {
          logger.debug(`[useWorkspaceChat] Develop complete:`, data.result);
          
          // Show completion in chat
          const filesCreated = data.result?.filesCreated?.length || 0;
          const filesModified = data.result?.filesModified?.length || 0;
          const previewUrlVal = data.result?.previewUrl;
          
          let completionMessage = data.result?.success 
            ? `✅ Feature development complete!\n- Files created: ${filesCreated}\n- Files modified: ${filesModified}`
            : `⚠️ Feature development stopped: ${data.result?.pauseReason || 'unknown'}`;
          
          if (previewUrlVal) {
            completionMessage += `\n- Preview: ${previewUrlVal}`;
          }
          
          // Clear preview text and append completion message to last assistant message
          setPreviewText(convId, '');
          // Use updateLastMessage to append the completion message
          // We need to read the current buffer state -- use the updateBuffer pattern through context
          // For now, use a simple approach: updateLastMessage with formatted content
          updateLastMessage(convId, completionMessage);
          
          // Clean up
          websocketService.unregisterJobHandlers(data.jobId);
          setStreamingProgress(convId, null);
          setIsProcessing(convId, false);
          streamControllersRef.current.delete(convId);
        },
        
        onDevelopPaused: (data: { jobId: string; reason: string; message: string; errors: string[] }) => {
          logger.debug(`[useWorkspaceChat] Develop paused: ${data.reason}`);
          
          setPreviewText(convId, `⏸️ Paused: ${data.message}`);
          
          // Append paused message to last assistant message
          updateLastMessage(convId, `⏸️ **Paused**: ${data.message}`);
          
          websocketService.unregisterJobHandlers(data.jobId);
          setStreamingProgress(convId, null);
          setIsProcessing(convId, false);
          streamControllersRef.current.delete(convId);
        },
      };
      
      const authToken = tokens?.accessToken || tokens?.idToken || '';
      
      // Debug: Log diagram context
      if (effectiveMode === 'diagram') {
        logger.debug('[useWorkspaceChat] Diagram mode - activeDiagramId:', activeDiagramId);
      }
      
      // For delegated modes, ensure WebSocket is connected with valid connectionId
      // Worker will stream results back via WebSocket using this connectionId
      // Unified delegation: agent, diagram, and plan modes all delegate to plan-worker
      let connectionId: string | undefined;
      const hasEditableLink = conversationLinks.some(l => 
        l.artifactType === 'document' || l.artifactType === 'ssa' || l.artifactType === 'coding' || l.artifactType === 'diagram' || l.artifactType === 'plan'
      );
      const needsWorkerConnection = effectiveMode === 'agent' || effectiveMode === 'diagram' || effectiveMode === 'plan' || hasEditableLink;
      
      if (needsWorkerConnection) {
        // Import deriveWebSocketUrl dynamically to get WS URL
        const { deriveWebSocketUrl } = await import('../services/streaming');
        const wsUrl = deriveWebSocketUrl();
        
        if (!wsUrl) {
          throw new Error('WebSocket URL not configured. Cannot use Agent mode.');
        }
        
        // Ensure WebSocket is connected and get fresh connectionId
        // This will reconnect if the connection was closed due to idle timeout
        try {
          connectionId = await websocketService.ensureConnectedWithId(wsUrl, authToken);
          logger.debug(`[useWorkspaceChat] Agent mode - connectionId: ${connectionId}`);
        } catch (wsError) {
          const errorMessage = wsError instanceof Error ? wsError.message : 'Unknown error';
          throw new Error(`Failed to establish WebSocket connection: ${errorMessage}`);
        }
      }

      // Link-based architecture: derive diagramId from link when available
      // Priority: link-derived > legacy effectiveMode === 'diagram' > documentContext
      const diagramLink = conversationLinks?.find(l => l.artifactType === 'diagram');
      const effectiveDiagramId = diagramLink?.artifactId || (effectiveMode === 'diagram' ? activeDiagramId : documentContext?.diagramId);
      
      const streamController = startAIStream(
        {
          message: userMessage.content,
          conversationId: convId,
          // Use documentContext projectId, or fallback to first allowedProject (for creative projects without documents)
          projectId: documentContext?.projectId || (allowedProjectIds.length > 0 ? allowedProjectIds[0] : undefined),
          documentNumber: documentContext?.documentNumber,
          revision: documentContext?.revision,
          // Include diagramId from link, diagram mode, or active diagram tab
          diagramId: effectiveDiagramId,
          documentContext: {
            ...documentContext,
            diagramId: effectiveDiagramId,
          },
          model: selectedModel,
          aiMode: effectiveMode,
          // Link-based architecture: send conversation links to backend
          // Backend derives capabilities from links when present
          links: conversationLinks,
          allowedScopes,
          allowedProjectIds,
          loadMemoryContext: true,  // Always load memory context
          // Agent mode: include connectionId for worker delegation
          // Link-based architecture: also include when document/SSA is linked
          connectionId: needsWorkerConnection ? connectionId : undefined,
          // Persona: include personaId for dynamic prompt generation
          personaId: (() => {
            logger.debug('[useWorkspaceChat] Sending personaId:', pageContext.personaId);
            return pageContext.personaId;
          })(),
          // Debug: force worker chaining after N turns for testing
          // DISABLED - natural 14-minute timeout triggers handoff
          // forceChainAfterTurns: aiMode === 'agent' ? 2 : undefined,
        },
        authToken,
        streamHandlers
      );
      
      // Store stream controller by conversation ID (not a global singleton)
      streamControllersRef.current.set(convId, streamController);
      
      try {
        await streamController.promise;
      } finally {
        streamControllersRef.current.delete(convId);
      }
    } catch (error: unknown) {
      updateLastMessage(convId!, `Error: ${(error as Error).message}`);
      setIsProcessing(convId!, false);
      if (convId) streamControllersRef.current.delete(convId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addMessage,
    activeConversationId,
    ensureBuffer,
    createConversation,
    pageContext,
    navigate,
    setIsProcessing,
    setStreamingProgress,
    setThinkingPhase,
    addThinkingActivity,
    updateThinkingActivities,
    clearThinkingActivities,
    setPreviewText,
    setDebugState,
    updateLastMessage,
    scrollToBottom,
    buildDocumentContext,
    selectedModel,
    aiMode,
    allowedScopes,
    allowedProjectIds,
    messages.length,
    loadConversations,
    updateConversationTitle,
  ]);

  /**
   * Stop the current AI generation
   * Returns a Promise that resolves when cleanup is complete (with timeout for robustness)
   * 
   * Implements graceful cancellation:
   * 1. Aborts the frontend stream immediately (stops receiving data)
   * 2. Sends cancellation request to backend (graceful server-side stop)
   * 3. Backend allows current tool to complete, then exits loop
   */
  /**
   * Stop generation for a specific conversation (or the active one)
   */
  const stopGenerationForConversation = useCallback(async (targetConvId?: string): Promise<void> => {
    const convId = targetConvId || activeConversationId;
    if (!convId) return;

    // 1. Abort client-side stream immediately (stops UI updates)
    const controller = streamControllersRef.current.get(convId);
    if (controller) {
      controller.abort();
      streamControllersRef.current.delete(convId);
    }
    
    // 2. Send cancellation to backend (graceful server-side stop)
    // Fire-and-forget - don't block UI on backend acknowledgment
    authService.getTokens()
      .then(tokens => {
        const token = tokens?.accessToken || tokens?.idToken || '';
        if (token) {
          cancelConversation(convId, token)
            .then(result => {
              if (result.acknowledged) {
                logger.debug('[stopGeneration] Backend cancellation acknowledged for', convId);
              }
            })
            .catch(err => logger.warn('[stopGeneration] Backend cancel failed:', err));
        }
      })
      .catch(err => logger.warn('[stopGeneration] Failed to get tokens for cancel:', err));
    
    // 3. Clear UI state immediately (don't wait for backend)
    setIsProcessing(convId, false);
    setThinkingSteps([]);
    setPreviewText(convId, '');
    setThinkingPhase(convId, 'idle');
    clearThinkingActivities(convId);
    setStreamingProgress(convId, null);
    
    // Mark last message as stopped (subtle visual indicator)
    markLastMessageStopped(convId);
    
    // Small delay for network cleanup
    await new Promise<void>(resolve => setTimeout(resolve, 50));
  }, [activeConversationId, setIsProcessing, markLastMessageStopped, setPreviewText, setThinkingPhase, clearThinkingActivities, setStreamingProgress]);

  // Register stopGeneration with AIContext for each active conversation
  // This allows AIContext to stop generation when tabs are closed or conversations deleted
  const stopGeneration = stopGenerationForConversation;

  /**
   * Clear thinking steps
   */
  const clearThinkingSteps = useCallback(() => {
    setThinkingSteps([]);
  }, []);

  /**
   * Clear thinking overlay state for the active conversation
   */
  const clearThinkingOverlay = useCallback(() => {
    if (!activeConversationId) return;
    setPreviewText(activeConversationId, '');
    setThinkingPhase(activeConversationId, 'idle');
    clearThinkingActivities(activeConversationId);
    setDebugState(activeConversationId, { chainCount: 0, turnCount: 0, elapsedMs: 0 });
  }, [activeConversationId, setPreviewText, setThinkingPhase, clearThinkingActivities, setDebugState]);

  return {
    thinkingSteps,
    sendMessage,
    stopGeneration,
    clearThinkingSteps,
    // ThinkingOverlay state (derived from active buffer via context)
    previewText,
    thinkingPhase,
    thinkingActivities,
    debugState,
    clearThinkingOverlay,
  };
}
